(ns dre.pages.dictionary.views
  (:require
   ["antd" :refer [Spin]]
   [clojure.string :as str]
   [dre.app.events :as app]
   [dre.app.router :as router :refer [->route ->route-with-query-params]]
   [dre.components.collapse :refer [collapse]]
   [dre.components.icon :refer [fa-icon]]
   [dre.components.table :refer [table]]
   [dre.components.tooltip :refer [tooltip-wrapper]]
   [dre.pages.dictionary.events :as events]
   [dre.pages.dictionary.term-meta :as ddm]
   [dre.pages.dictionary.term :as ddt]
   [dre.pages.dictionary.util :as tutil]
   [dre.pages.dictionary.term-sind :as sind]
   [dre.pages.dictionary.term-sadv :as sadv]
   [dre.pages.dictionary.subclass-graph :refer [subclass-graph]]
   [dre.i18n.views :refer [tr]]
   [dre.i18n.events :as-alias i18n]
   [dre.utils.bem :as bem]
   [dre.utils :as utils]
   [re-frame.core :as rf]
   [reagent.core :as r]))

(def component-name :dictionary)

(defn primary-information
  [data]
  [:div
   {:style {:width "100%"}}
   [ddt/term-content-header (get data :term)]
   [ddt/vocabs-row (get-in data [:term])]
   [ddt/alt-labels-row (get-in data [:term])]
   [ddt/drug-combinations-row (get-in data [:term])]
   [ddt/also-included-row (get-in data [:blueprints :completed-pill])]
   [ddt/acronyms-row (get-in data [:acronyms])]
   [ddt/definitions-row (get data :term)]])

(defn docsearch-metadata
  [data admin-role?]
  [:div
   {:style {:width "100%"}}
   [ddm/sense-row (get data :term) admin-role?]
   [ddm/uri-row (get data :term) admin-role?]
   [ddm/semgroup-row (get data :sense-details)]
   [ddm/semtype-row (get data :term)]
   [ddm/umls-cui-row (get data :term)]])

(defn drugbank-metadata
  [data props]
  [:div
   {:style {:width "100%"}}
   (ddm/metadata-rows (get-in data [:term :label-map]) (get-in data [:term :guid-map]) props)])

(defn indications
  [data props]
  (let [sind-map (tutil/items-map props sind/indications-map)
        head (tutil/table-head-row sind-map)
        rows (tutil/table-content-rows sind-map props)]
    [:div
     {:style {:width "100%"}}
     [table
      {:columns (map (fn [v]
                       {:title (:label v)
                        :render (fn [data]
                                  (get data (:key v)))}) head)
       :dataSource rows
       :pagination {:defaultPageSize 10
                    :hideOnSinglePage true}}]]))

(defn adverse-effects
  [data props]
  (let [sadv-map (tutil/items-map props sadv/effects-map)
        head (tutil/table-head-row sadv-map)
        rows (tutil/table-content-rows sadv-map props)]
    [:div
     {:style {:width "100%"}}
     [table
      {:columns (map (fn [v]
                       {:title (:label v)
                        :render (fn [data]
                                  (get data (:key v)))}) head)
       :dataSource rows
       :pagination {:defaultPageSize 10
                    :hideOnSinglePage true}}]]))

(defn umls-metadata
  [data props]
  [:div
   {:style {:width "100%"}}
   (ddm/metadata-rows (get-in data [:term :label-map]) (get-in data [:term :guid-map]) props)])

(defn misc-metadata
  [data props]
  [:div
   {:style {:width "100%"}}
   (ddm/metadata-rows (get-in data [:term :label-map]) (get-in data [:term :guid-map]) props)])

(defn ancestors-graph
  [data]
  [:div
   {:style {:width "100%" :height @(rf/subscribe [::events/subclass-height])}}
   (if (> (count (:children (:dict/gather-by-cui (get-in data [:subclass])))) 0)
     [subclass-graph (get data :subclass)]
     [:p [tr "No relevant hierarchy"]])])

(defn sections
  [data admin-role?]
  (let [props (ddm/prepare-terms-meta (get-in data [:term :terms]))
        prefixed (fn [prefix]
                   #(str/starts-with? (str/lower-case (first %))
                                      (str/lower-case prefix)))]
    (remove nil? [{:label (r/as-element [:b [tr "Primary information"]])
                   :children (r/as-element [primary-information data])}
                  {:label (r/as-element [:b [tr "Literature Search metadata"]])
                   :children (r/as-element [docsearch-metadata data admin-role?])}
                  (when-let [drugbank-props (seq (->> props
                                                      (filter (prefixed "drugbank"))
                                                      (remove (prefixed "drugbank/Structured"))))]
                    {:label (r/as-element [:b [tr "Drugbank metadata"]])
                     :children (r/as-element [drugbank-metadata data drugbank-props])})
                  (when-let [indication-props (seq (->> (get props "drugbank/Structured indications")))]
                    {:label (r/as-element [:b [tr "Indications"]])
                     :children (r/as-element [indications data indication-props])})
                  (when-let [ae-props (seq (->> (get props "drugbank/Structured adverse effects")))]
                    {:label (r/as-element [:b [tr "Adverse effects"]])
                     :children (r/as-element [adverse-effects data ae-props])})
                  (when-let [umls-props (seq (->> props (filter (prefixed "umls"))))]
                    {:label (r/as-element [:b [tr "UMLS metadata"]])
                     :children (r/as-element [umls-metadata data umls-props])})
                  (when-let [misc-props (seq (->> props
                                                  (remove (prefixed "drugbank"))
                                                  (remove (prefixed "umls"))))]
                    {:label (r/as-element [:b [tr "Misc metadata"]])
                     :children (r/as-element [misc-metadata data misc-props])})
                  {:label (r/as-element [:b [tr "Ancestors graph"] " (" (get-in data [:subclass :dict/graph-name]) ")"])
                   :children (r/as-element [ancestors-graph data])}])))

(defn view
  []
  (let [show-uris? (r/atom false)]
    (fn []
      (let [data @(rf/subscribe [::events/data])
            loading? @(rf/subscribe [::events/loading?])
            user @(rf/subscribe [::app/user])
            admin-role? (utils/admin-role? user)
            route-params (events/route-params (get-in data [:term :sense]) nil)]
        [:div
         {:class component-name}
         [:div
          {:class (bem/be component-name :container)}
          (if loading?
            [:div
             {:class (bem/be component-name :loading)}
             [:> Spin]]
            [:<>
             [:div
              {:class (bem/be component-name :title)}
              [:div
               {:class (bem/be component-name :title-label)}
               (let [canonical-label (get-in data [:term :canonical-label])
                     title (get-in data [:term :title])]
                 [:span [:span title]
                  (when (and canonical-label (not= title canonical-label))
                    [:span (str " (" canonical-label ") ")])])]
              (when (:is-modal? data)
                [tooltip-wrapper "Open in new window"
                 [:a {:class (bem/be component-name :title-open)
                      :href (->route-with-query-params (->route :dictionary {:guid (:guid route-params)}) (:query-params route-params))
                      :target "_blank"}
                  [fa-icon :external-link]]])
              (when admin-role?
                [tooltip-wrapper "Show debug information"
                 [:div
                  {:on-click #(swap! show-uris? not)
                   :class [(bem/be component-name :title-dev)
                           (when @show-uris? (bem/bem component-name :title-dev :active))]}
                  [fa-icon :code]]])]
             [:div
              {:class (bem/be component-name :content)}
              (when @show-uris?
                [:pre
                 [:strong "DocSearch sense"]
                 [:br]
                 (into [:<>] (->> (ddm/term-senses {:terms (get-in data [:term :terms])})
                                  (interpose [:br])))
                 [:br]
                 [:strong "Uniform Resource Identifiers"]
                 [:br]
                 (into [:<>] (->> (distinct (get-in data [:term :terms]))
                                  (map (fn [t] [ddm/term-link t]))
                                  (interpose [:br])))])
              [collapse
               (sections data admin-role?)]
              [ddt/term-license-note]]])]]))))
